
import { useState } from "react";
import { Collection } from "@/lib/api/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CollectionForm } from "./forms/CollectionForm";
import { useAuth } from "@/components/auth/AuthProvider";

interface CollectionManagementProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  editingCollection?: Collection | null;
  onSuccess?: (collectionId: string) => void;
}

export const CollectionManagement = ({ 
  open, 
  onOpenChange,
  editingCollection,
  onSuccess
}: CollectionManagementProps) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, profile } = useAuth();

  const handleSubmit = async (values: { 
    name: string; 
    description?: string;
    custom_generator_model?: string;
    custom_improver_model?: string;
  }) => {
    if (!user) {
      console.error("No authenticated user found");
      toast.error("You must be logged in to create a collection");
      return;
    }

    if (isSubmitting) {
      console.log("Submission already in progress");
      return;
    }
    
    setIsSubmitting(true);
    try {
      console.log('Submitting collection:', { values, userId: user.id, orgId: profile?.organization_id });

      if (editingCollection) {
        const { error } = await supabase
          .from("collections")
          .update({
            name: values.name,
            description: values.description,
            custom_generator_model: values.custom_generator_model || null,
            custom_improver_model: values.custom_improver_model || null,
          })
          .eq("id", editingCollection.id)
          .select()
          .single();

        if (error) throw error;

        toast.success('Collection updated successfully');
      } else {
        // Create the collection data object
        const collectionData: any = {
          name: values.name,
          description: values.description,
          user_id: user.id,
          custom_generator_model: values.custom_generator_model || null,
          custom_improver_model: values.custom_improver_model || null,
        };

        // Only add organization_id if it exists and is not null
        if (profile?.organization_id) {
          collectionData.organization_id = profile.organization_id;
        }

        console.log('Creating collection with data:', collectionData);

        const { data, error } = await supabase
          .from("collections")
          .insert(collectionData)
          .select()
          .single();

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        console.log('Collection created:', data);
        toast.success('Collection created successfully');

        if (data) {
          await queryClient.invalidateQueries({ queryKey: ["collections"] });
          if (onSuccess) {
            onSuccess(data.id);
          } else {
            onOpenChange?.(false);
            navigate(`/collections/${data.id}`);
          }
        }
      }

      await queryClient.invalidateQueries({ queryKey: ["collections"] });
      onOpenChange?.(false);
    } catch (error: any) {
      console.error('Error saving collection:', error);
      toast.error(error.message || 'Failed to save collection');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingCollection ? "Edit Collection" : "Create Collection"}
          </DialogTitle>
          <DialogDescription>
            {editingCollection 
              ? "Update your collection details below."
              : "Create a new collection to organize your products."}
          </DialogDescription>
        </DialogHeader>
        <CollectionForm
          editingCollection={editingCollection}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
        />
      </DialogContent>
    </Dialog>
  );
};
